import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, ArrowRight, Calendar, FileText, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Carousel images
const heroImages = [
  'https://customer-assets.emergentagent.com/job_github-sync-86/artifacts/yz5su02q_ChatGPT%20Image%20May%2020%2C%202026%2C%2003_21_39%20PM.png', // Building exterior (new)
  'https://customer-assets.emergentagent.com/job_4490e562-0dc5-408b-9755-d5149a4b3b2d/artifacts/gbzx1tdw_15188_2204240158013.webp', // Lounge
  'https://customer-assets.emergentagent.com/job_4490e562-0dc5-408b-9755-d5149a4b3b2d/artifacts/olimxcmt_15188_0405220520092.webp', // Bedroom
  'https://customer-assets.emergentagent.com/job_4490e562-0dc5-408b-9755-d5149a4b3b2d/artifacts/pu6mukqb_15188_2204240158016.webp', // Garden 1
  'https://customer-assets.emergentagent.com/job_4490e562-0dc5-408b-9755-d5149a4b3b2d/artifacts/58vt5ee2_15188_2204240158018.webp', // Garden 2
];

const HeroSection = () => {
  const [visitOpen, setVisitOpen] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change image every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Visit form
  const [visitForm, setVisitForm] = useState({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', care_type: '', additional_info: '' });
  const [visitSubmitting, setVisitSubmitting] = useState(false);

  // Brochure form
  const [brochureForm, setBrochureForm] = useState({ name: '', email: '', phone: '', address: '' });
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);

  // Enquiry form
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);

  const handleVisitSubmit = async (e) => {
    e.preventDefault();
    setVisitSubmitting(true);
    try {
      await axios.post(`${API}/visit-booking`, visitForm);
      toast.success('Visit booking request sent! We will confirm your appointment soon.');
      setVisitForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', care_type: '', additional_info: '' });
      setVisitOpen(false);
    } catch (error) {
      toast.error('Failed to submit booking request. Please try again.');
    } finally {
      setVisitSubmitting(false);
    }
  };

  const handleBrochureSubmit = async (e) => {
    e.preventDefault();
    setBrochureSubmitting(true);
    try {
      await axios.post(`${API}/brochure-request`, brochureForm);
      toast.success('Brochure request sent! You will receive it shortly.');
      setBrochureForm({ name: '', email: '', phone: '', address: '' });
      setBrochureOpen(false);
    } catch (error) {
      toast.error('Failed to request brochure. Please try again.');
    } finally {
      setBrochureSubmitting(false);
    }
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setEnquirySubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, { ...enquiryForm, enquiry_type: 'general' });
      toast.success('Enquiry sent successfully! We will be in touch soon.');
      setEnquiryForm({ name: '', email: '', phone: '', message: '' });
      setEnquiryOpen(false);
    } catch (error) {
      toast.error('Failed to send enquiry. Please try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center visible"
      data-testid="hero-section"
    >
      {/* Background Image Carousel */}
      {heroImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url('${image}')`,
          }}
        />
      ))}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#1e3a5f]/40" />

      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex 
                ? 'bg-[#c9a227] w-8' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          {/* Operator Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6 animate-fade-in">
            Now newly operated by RSJB Nursing Homes
          </div>

          {/* Main Heading */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-slide-up"
           
          >
            Abney Low<br />
            <span className="text-[#c9a227]">Nursing Home</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-white/90 mb-4 animate-slide-up delay-100">
            Compassionate Nursing, Residential & Dementia Care in Hoylake, Wirral
          </p>

          {/* Description */}
          <p className="text-base text-white/80 mb-8 max-w-2xl animate-slide-up delay-200">
            Providing safe, high-quality, person-centred care in a warm and welcoming environment where residents feel truly at home.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center gap-4 mb-10 animate-slide-up delay-300">
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 backdrop-blur-sm rounded-full text-white">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium">CQC Rated Good</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white">
              <Check className="w-4 h-4 text-[#c9a227]" />
              <span className="text-sm font-medium">24/7 Nursing Care</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white">
              <Check className="w-4 h-4 text-[#c9a227]" />
              <span className="text-sm font-medium">Personalised Care Plans</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 animate-slide-up delay-400">
            <Button
              onClick={() => setVisitOpen(true)}
              className="bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid="hero-book-visit-btn"
            >
              Book a Visit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => setBrochureOpen(true)}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
              data-testid="hero-brochure-btn"
            >
              Request a Brochure
            </Button>
            <Button
              onClick={() => setEnquiryOpen(true)}
              variant="ghost"
              className="text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
              data-testid="hero-contact-btn"
            >
              Speak to Our Team
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse" />
        </div>
      </div>

      {/* Book a Visit Dialog */}
      <Dialog open={visitOpen} onOpenChange={setVisitOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white border border-slate-200 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1e3a5f]">
              Book a Visit
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVisitSubmit} className="space-y-4 mt-4" data-testid="hero-visit-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={visitForm.name} onChange={(e) => setVisitForm({ ...visitForm, name: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={visitForm.email} onChange={(e) => setVisitForm({ ...visitForm, email: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={visitForm.phone} onChange={(e) => setVisitForm({ ...visitForm, phone: e.target.value })} required className="mt-2 rounded-xl" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Preferred Date *</Label>
                <Input type="date" value={visitForm.preferred_date} onChange={(e) => setVisitForm({ ...visitForm, preferred_date: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label>Preferred Time *</Label>
                <Select value={visitForm.preferred_time} onValueChange={(value) => setVisitForm({ ...visitForm, preferred_time: value })}>
                  <SelectTrigger className="mt-2 rounded-xl"><SelectValue placeholder="Select time" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (9am - 12pm)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                    <SelectItem value="evening">Evening (5pm - 7pm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Type of Care Required *</Label>
              <Select value={visitForm.care_type} onValueChange={(value) => setVisitForm({ ...visitForm, care_type: value })}>
                <SelectTrigger className="mt-2 rounded-xl"><SelectValue placeholder="Select care type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nursing">Nursing Care</SelectItem>
                  <SelectItem value="residential">Residential Care</SelectItem>
                  <SelectItem value="dementia_nursing">Dementia Nursing</SelectItem>
                  <SelectItem value="dementia_residential">Dementia Residential</SelectItem>
                  <SelectItem value="mental_health_nursing">Mental Health Nursing</SelectItem>
                  <SelectItem value="bariatric_nursing">Bariatric Nursing</SelectItem>
                  <SelectItem value="unsure">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional Information</Label>
              <Textarea value={visitForm.additional_info} onChange={(e) => setVisitForm({ ...visitForm, additional_info: e.target.value })} className="mt-2 rounded-xl" placeholder="Any specific requirements?" />
            </div>
            <Button type="submit" disabled={visitSubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3">
              {visitSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</> : <><Calendar className="w-5 h-5 mr-2" />Book Visit</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Request Brochure Dialog */}
      <Dialog open={brochureOpen} onOpenChange={setBrochureOpen}>
        <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1e3a5f]">
              Request a Brochure
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBrochureSubmit} className="space-y-4 mt-4" data-testid="hero-brochure-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={brochureForm.name} onChange={(e) => setBrochureForm({ ...brochureForm, name: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={brochureForm.email} onChange={(e) => setBrochureForm({ ...brochureForm, email: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={brochureForm.phone} onChange={(e) => setBrochureForm({ ...brochureForm, phone: e.target.value })} className="mt-2 rounded-xl" />
            </div>
            <div>
              <Label>Postal Address (for printed brochure)</Label>
              <Textarea value={brochureForm.address} onChange={(e) => setBrochureForm({ ...brochureForm, address: e.target.value })} className="mt-2 rounded-xl" placeholder="Enter your address" />
            </div>
            <Button type="submit" disabled={brochureSubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3">
              {brochureSubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Requesting...</> : <><FileText className="w-5 h-5 mr-2" />Request Brochure</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Speak to Our Team Dialog */}
      <Dialog open={enquiryOpen} onOpenChange={setEnquiryOpen}>
        <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1e3a5f]">
              Speak to Our Team
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEnquirySubmit} className="space-y-4 mt-4" data-testid="hero-enquiry-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={enquiryForm.name} onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={enquiryForm.email} onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })} required className="mt-2 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={enquiryForm.phone} onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })} required className="mt-2 rounded-xl" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={enquiryForm.message} onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })} required className="mt-2 rounded-xl min-h-[120px]" placeholder="How can we help you?" />
            </div>
            <Button type="submit" disabled={enquirySubmitting} className="w-full bg-[#c9a227] text-[#1e3a5f] hover:bg-[#d4b03a] rounded-full py-3">
              {enquirySubmitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending...</> : <><Send className="w-5 h-5 mr-2" />Send Enquiry</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HeroSection;
